import React, { useCallback, useRef, useEffect } from 'react';

import { createPopper } from '@popperjs/core';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import { isWebkit } from '@Sage/ssm-utils';

import { intercomHideAndShow } from 'utils/custom';

import styles from './styles';
const useStyles = createUseStyles(styles, 'Actions');

const PLACEMENTS = { left: 'top-start', center: 'top', right: 'top-end', bottom: 'bottom' };

const Actions = ({
    actions,
    hideActions,
    hoveredRowInfo,
    id,
    dataGridRef,
    rowData,
    isLazyLoaded,
    intercomRef,
    isLoading,
    forceActionsRefresh,
}) => {
    const classes = useStyles();
    let popperInstance = useRef(null);
    const containerClasses = useRef();
    containerClasses.current = [classes.actionsContainer];

    if (hoveredRowInfo?.hasScroll)
        containerClasses.current.push(isWebkit() ? classes.customScroll : classes.nativeScroll);
    if (hideActions) containerClasses.current.push(classes.hideActions);

    const containerId = id ? `w5-actions-component-${id}` : 'w5-actions-component';

    useEffect(() => {
        // IF forceActionsRefresh is enabled:
        // Reenable the mouse events in the actions container if they were disabled
        const isDisabled = containerClasses.current.indexOf(classes.disabled);
        if (forceActionsRefresh && !isLoading && isDisabled !== -1) {
            setTimeout(() => {
                containerClasses.current.splice(isDisabled, 1);
            });
        }

        if (dataGridRef) {
            const tooltip = dataGridRef.querySelector('.w5-tooltip');
            tooltip.style.display = 'none';
        }
        // Reloading when rowData or isLazyLoaded changes
    }, [
        actions,
        dataGridRef,
        rowData,
        isLazyLoaded,
        classes.disabled,
        isLoading,
        forceActionsRefresh,
        hoveredRowInfo?.data,
    ]);

    const onMouseClick = useCallback(() => {
        // IF forceActionsRefresh is enabled:
        // Disable mouse events in the actions container so it triggers the mouseEnter event in the underlying table cell and the hovered row actions are updated
        forceActionsRefresh && containerClasses.current.push(classes.disabled);
    }, [classes.disabled, containerClasses, forceActionsRefresh]);

    const onMouseEnter = useCallback(
        (e) => {
            const table = dataGridRef || e.target.closest('[class*=SectionList__root]');
            const rows = table.querySelectorAll('.ag-row--action-hover');
            for (let row of rows) {
                row.classList?.remove('ag-row--action-hover');
            }
            hoveredRowInfo?.rowElement?.classList?.add('ag-row--action-hover');
        },
        [dataGridRef, hoveredRowInfo?.rowElement?.classList],
    );

    const onMouseLeave = useCallback(
        (e) => {
            if (intercomRef) intercomHideAndShow('show');
            const table = dataGridRef || e.target.closest('[class*=SectionList__root]');
            const rows = table.querySelectorAll('.ag-row--action-hover');
            for (let row of rows) {
                row.classList?.remove('ag-row--action-hover');
            }
        },
        [dataGridRef, intercomRef],
    );

    const onMouseEnterIcon = useCallback(
        (e, data) => {
            if (intercomRef) intercomHideAndShow(intercomRef);
            e.preventDefault();
            e.stopPropagation();
            if (!data) return;

            const target = e.target.closest('[class*=actionsIcon]');
            const table = dataGridRef || e.target.closest('[class*=SectionList__root]');
            const tooltip = table.querySelector('.w5-tooltip');
            const actionsContainer = e.target.closest('[class*=actionsContainer]');

            hoveredRowInfo?.rowElement?.classList?.add('ag-row--action-hover');
            actionsContainer.style.display = 'block';
            tooltip.style.display = 'block';
            tooltip.firstElementChild.innerHTML = data.content;

            createPopper(target, tooltip, {
                placement: PLACEMENTS[data.placement] || PLACEMENTS['center'],
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 5],
                        },
                    },
                ],
            });
        },
        [dataGridRef, hoveredRowInfo, intercomRef],
    );

    const onMouseLeaveIcon = useCallback(
        (e) => {
            const table = dataGridRef || e.target.closest('[class*=SectionList__root]');
            const tooltip = table.querySelector('.w5-tooltip');
            tooltip.style.display = 'none';
            if (popperInstance.current) {
                popperInstance.current.destroy();
                popperInstance.current = null;
            }
        },
        [dataGridRef],
    );

    return (
        <div
            id={containerId}
            className={[containerClasses.current.join(' ')]}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {hoveredRowInfo?.data && (
                <div className={classes.actionsContent}>
                    {actions.map((current, index) => {
                        const action = current(hoveredRowInfo.data);
                        if (!action) return null;
                        return (
                            <div
                                key={index}
                                className={classes.actionsIcon}
                                onMouseEnter={(e) => onMouseEnterIcon(e, action.tooltip)}
                                onMouseLeave={onMouseLeaveIcon}
                                onClick={onMouseClick}
                            >
                                {action.icon}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default React.memo(Actions);
